import { dayjsWithExtensions } from './dayjs-config';
import type { Dayjs } from 'dayjs';

/**
 * Returns a dayjs instance in the local timezone representing the same calendar date
 * as the given UTC epoch timestamp (useful for initializing DatePickers for all-day events).
 */
export function getLocalDayjsFromAllDayEpoch(utcEpoch: number): Dayjs {
  return dayjsWithExtensions(
    dayjsWithExtensions.utc(utcEpoch).format('YYYY-MM-DD')
  );
}

/**
 * Returns a UTC epoch timestamp representing midnight of the given local epoch timestamp.
 * (useful when a user selects a date locally but we need to submit an all-day UTC timestamp)
 */
export function getAllDayEpochFromLocalEpoch(localEpoch: number): number {
  return dayjsWithExtensions
    .utc(dayjsWithExtensions(localEpoch).format('YYYY-MM-DD'))
    .valueOf();
}

export interface EventTimePayload {
  dateTime?: Dayjs | Date | number | string | null;
  allDay?: boolean;
}

/**
 * Helper to safely format the start payload for calendar events.
 */
export function formatEventStartPayload<T extends EventTimePayload>(
  start?: T
): (Omit<T, 'dateTime'> & { dateTime: number }) | undefined {
  if (!start) return undefined;

  const timeValue = start.dateTime
    ? dayjsWithExtensions(start.dateTime).valueOf()
    : 0;

  return {
    ...start,
    allDay: !!start.allDay,
    dateTime:
      start.allDay && start.dateTime
        ? getAllDayEpochFromLocalEpoch(timeValue)
        : timeValue,
  } as Omit<T, 'dateTime'> & { dateTime: number };
}

/**
 * Helper to safely format the end payload for calendar events.
 */
export function formatEventEndPayload<T extends EventTimePayload>(
  start?: EventTimePayload,
  end?: T
): (Omit<T, 'dateTime'> & { dateTime: number }) | undefined {
  if (!end) return undefined;

  const timeValue = end.dateTime
    ? dayjsWithExtensions(end.dateTime).valueOf()
    : 0;

  return {
    ...end,
    allDay: !!start?.allDay,
    dateTime:
      start?.allDay && end.dateTime
        ? getAllDayEpochFromLocalEpoch(
            dayjsWithExtensions(timeValue).add(1, 'day').valueOf()
          )
        : timeValue,
  } as Omit<T, 'dateTime'> & { dateTime: number };
}

/**
 * Returns a strictly formatted YYYY-MM-DD string from a UTC epoch.
 * (useful for mapping events into FullCalendar's all-day format)
 */
export function getAllDayDateStringFromEpoch(utcEpoch: number): string {
  return dayjsWithExtensions.utc(utcEpoch).format('YYYY-MM-DD');
}

/**
 * Formats a UTC epoch into the display label used in event dialogs.
 */
export function formatAllDayDisplayLabel(utcEpoch: number): string {
  return dayjsWithExtensions.utc(utcEpoch).format('dddd, MMMM D');
}
